<script setup lang="ts">
/**
 * @deprecated This component is deprecated and will be removed in the future.
 */
import {computed, defineProps} from 'vue';
import {useAssetStore} from "@/stores/assetStore";
import {usePlanetStore} from "@/stores/planetStore";
import {useShipDesignStore} from "@/stores/shipDesignStore.ts";
import { usePQAStore } from '@/stores/PQAStore.ts'
import { CDN } from '@/models/cdn.ts'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  },
  showPQA: {
    type: Boolean,
    default: true
  }
});

/**
 * Stores / models
 */
const assetStore = useAssetStore()
const planetStore = usePlanetStore()
const shipDesignStore = useShipDesignStore()
const PQAStore = usePQAStore()

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props?.planetId))
const shipDesignData = computed(() => shipDesignStore.getShipDesignById(planetData.value?.productionShipDesignId ?? 0))
const productionObjectData = computed(() => assetStore.findProductionObjectById(planetData.value?.productionObjectId ?? 0))

const widthPercentage = computed(() => {
  let progress = 0;
  if (!planetData.value) return 0;
  if (shipDesignData.value) {
    progress = (planetData.value.productionProgress / shipDesignData.value.productionCost);
  } else if (productionObjectData.value) {
    progress = (planetData.value.productionProgress / productionObjectData.value.productionCost)
  }
  if(progress > 1) {
    progress = 1
  }
  return progress * 100
});

const name = computed(() => {
  if (shipDesignData.value) {
    return shipDesignData.value.name
  } else if (productionObjectData.value?.type === 'building') {
    return t(`buildings.buildings.${productionObjectData.value.objectId}.name`)
  } else {
    return t('planets.production.ship.name')
  }
});

const imageUrl = computed(() => {
  if (shipDesignData.value?.frameCode) {
    return CDN.shipComponent(64, shipDesignData.value.frameCode)
  } else if (productionObjectData.value?.type === 'building') {
    return CDN.building(64, productionObjectData.value.objectId)
  }
  return ''
});

const pqaName = computed(() => {
  const pqa = PQAStore.findQueueById(planetData.value?.planetQueueAutomationId ?? 0)
  if(pqa) {
    return pqa.name
  }
  return ''
})

</script>

<template>
  <div v-if="planetData?.productionObjectId != 0" class="w-full pr-2">
    <div class="pt-0.5">
      <div class="flex-grow flex">
        <div v-if="imageUrl" class="flex-shrink-0 mr-1.5 mt-0.5">
          <StyledImage :src="imageUrl" size="sm" />
        </div>
        <div class="flex-grow flex flex-col min-w-0">
          <div class="flex-shrink">
            <div class="flex">
              <div class="flex-grow text-xs truncate">
                {{ name }}
              </div>
            </div>
          </div>
          <div class="flex-1 flex items-center justify-center pt-0.5">
              <div class="w-full rounded progress-track">
                <div class="progress-fill rounded" :style="'width:' + widthPercentage + '%'"></div>
              </div>
          </div>
          <div v-if="planetData?.planetQueueAutomationId && showPQA" class="flex-shrink">
            <div class="flex">
              <div class="flex-grow text-xxs text-subtext truncate">
                {{ pqaName }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="w-full pr-2">
    <div class="pt-0.5">
      <div class="flex-grow flex">
        <div class="flex-shrink-0 mr-1.5">
          <div class="h-9 w-9 mt-0.5"></div>
        </div>
        <div class="flex-grow flex flex-col min-w-0">
          <div class="flex-shrink">
            <div class="flex">
              <div class="flex-grow text-xs">
                &nbsp;
              </div>
            </div>
          </div>
          <div class="flex-1 flex items-center justify-center pt-0.5">
            <div class="w-full rounded">
              <div class="text-xs text-blue-100 text-center p-0.5 ps-2 leading-none rounded whitespace-nowrap" style="width:0%"></div>
            </div>
          </div>
          <div v-if="planetData?.planetQueueAutomationId && showPQA" class="flex-shrink">
            <div class="flex">
              <div class="flex-grow text-xxs text-subtext truncate">
                {{ pqaName }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.progress-track {
  height: 6px;
  background: rgba(20, 25, 35, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.4);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(95, 108, 120, 0.9) 0%,
    rgba(82, 95, 107, 0.85) 100%
  );
  box-shadow: 0 0 6px rgba(82, 95, 107, 0.5);
  transition: width 0.3s ease-out;
}
</style>
